/**
 * A select opened while its list is still closing opens once it closed (#255).
 *
 * Web Awesome's select (3.14) closes its list with a short animation (`--t-fast`, 120 ms) and
 * hides the list when the animation ends. If the person opens the select again before that (picks
 * an option and clicks the field right away to change their mind), the list shows and then the
 * end of the closing hides it: the select says it is open, with no list, until two more clicks.
 * The layer holds that opening (cancels its `wa-show`) and opens the select when its `wa-after-hide`
 * arrives, so a closing never hides a list the person just opened. No select waits more than
 * `MAX_CLOSING_MS`: if a closing never reports its end, the next opening goes ahead as usual.
 */

/** Longer than any closing animation: past it, a select is no longer taken as closing. */
export const MAX_CLOSING_MS = 1_000;

type WaSelect = HTMLElement & { open: boolean };

const isSelect = (target: EventTarget | null): target is WaSelect =>
  target instanceof HTMLElement && target.localName === 'wa-select';

const bound = new WeakSet<Node>();

/** Wires `root` (the page) once: its selects open only after their list finished closing. */
export function holdOpeningWhileClosing(
  root: Document | HTMLElement,
  now: () => number = () => performance.now(),
): void {
  if (bound.has(root)) return;
  bound.add(root);
  /** When each select started closing (until its list is hidden). */
  const closingSince = new WeakMap<WaSelect, number>();
  /** The selects the person opened while they were closing. */
  const waiting = new WeakSet<WaSelect>();
  root.addEventListener('wa-hide', (ev) => {
    // Cancelling a `wa-show` closes the select again: that is not a new closing.
    if (isSelect(ev.target) && !closingSince.has(ev.target)) closingSince.set(ev.target, now());
  });
  root.addEventListener('wa-after-hide', (ev) => {
    const select = ev.target;
    if (!isSelect(select)) return;
    closingSince.delete(select);
    if (!waiting.has(select)) return;
    waiting.delete(select);
    select.open = true;
  });
  root.addEventListener('wa-show', (ev) => {
    const select = ev.target;
    if (!isSelect(select)) return;
    const since = closingSince.get(select);
    if (since === undefined) return;
    if (now() - since > MAX_CLOSING_MS) {
      closingSince.delete(select);
      return;
    }
    ev.preventDefault();
    waiting.add(select);
  });
}
